import { MessageFilled } from "@ant-design/icons";
import { Flex, InputNumber } from "antd";
import { FC, useCallback } from "react";
import {
    Product,
    PromoTagProduct,
    TaskPromoTagPhotosEntity,
    TaskStatus,
} from "tm-shared/types";
import { formatUAHNumber } from "tm-shared/utils";

import { StatusCycleButton } from "@/domains/tasks";
import { StatusIndicator } from "@/domains/tasks";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import { StyledCommentIndicator, StyledTaskPromoTag } from "./Styled";

interface TaskPromoTagProps {
    product: PromoTagProduct;
    catalogProduct: Product | undefined;
    isActive: boolean;
    onChangeActive: (id: string) => void;
    payload: TaskPromoTagPhotosEntity | undefined;
    onChangeTaskStatus: (status: TaskStatus, barcode?: string) => void;
    onChangePerformerValue: (value: number | null, barcode: string) => void;
}

export const TaskPromoTag: FC<TaskPromoTagProps> = ({
    product,
    isActive,
    onChangeActive,
    payload,
    catalogProduct,
    onChangeTaskStatus,
    onChangePerformerValue,
}) => {
    const { barcode, price } = product;

    const hasComment = !!payload?.comment;
    const status = payload?.status ?? TaskStatus.Incomplete;
    const performerValue = payload?.performerValue ?? null;
    const photoCount = payload?.photos?.length ?? 0;
    const isPerformerValueError =
        status === TaskStatus.Completed && performerValue === null;

    const selectTask = useCallback(() => {
        onChangeActive(barcode);
    }, [barcode, onChangeActive]);

    const handleChangeTaskStatus = useCallback(
        (status: TaskStatus) => {
            onChangeTaskStatus(status, barcode);
        },
        [barcode, onChangeTaskStatus],
    );

    return (
        <StyledTaskPromoTag $isActive={isActive} onClick={selectTask}>
            <Flex vertical gap={4}>
                <PrimaryText>
                    {catalogProduct?.name} ({product.barcode})
                </PrimaryText>
                <Flex gap={36}>
                    <SecondaryText>Акційна ціна:</SecondaryText>

                    <PrimaryText>{formatUAHNumber(+price)}</PrimaryText>
                </Flex>

                <Flex gap={8} align="center">
                    <SecondaryText>Фактична ціна:</SecondaryText>

                    <InputNumber
                        style={{
                            width: 80,
                        }}
                        status={isPerformerValueError ? "error" : undefined}
                        styles={{
                            input: {
                                textAlign: "center",
                            },
                        }}
                        value={performerValue}
                        onChange={(value) =>
                            onChangePerformerValue(value, barcode)
                        }
                        disabled={status !== TaskStatus.Completed}
                    />
                </Flex>
            </Flex>

            <Flex justify="center" align="center">
                <StyledCommentIndicator>
                    {hasComment && (
                        <StatusIndicator type="warning">
                            <MessageFilled />
                        </StatusIndicator>
                    )}
                </StyledCommentIndicator>

                <StatusCycleButton
                    value={status}
                    onChange={handleChangeTaskStatus}
                    states={[TaskStatus.Incomplete, TaskStatus.Rejected]}
                    disabled={status === TaskStatus.Completed}
                    photoMode
                    photoCount={photoCount}
                    dimension={48}
                    border={false}
                />
            </Flex>
        </StyledTaskPromoTag>
    );
};
